import React, { createContext, useEffect, useContext, useReducer } from "react"

import { analytics } from "utils/analytics"
import { useCurrentUser } from "hooks/use-current-user"
import { useSwitchPlan } from "sdk/billing/mutations"

import { usePlanData } from "./hooks/use-plan-data"
import { discountTrackingProperties } from "./utils"

const MODALS = {
  UNAUTHORIZED: "unauthorized",
  PLAN_SWITCH_CONFIRMATION: "plan_switch_confirmation",
  PAYMENT: "payment",
  SIGNALS_OPT_IN: "signals_opt_in",
  CELEBRATION: "celebration",
  COMPARISON: "comparison"
}

const initialState = {
  serviceLevel: "creator",
  plansForPaymentForm: null,
  activeModal: MODALS.COMPARISON,
  signalsOptedIn: false
}

export function planComparisonReducer(state, action) {
  switch (action.type) {
    case "SET_SERVICE_LEVEL":
      return {
        ...state,
        serviceLevel: action.payload,
        plansForPaymentForm: action.plansForPaymentForm
      }
    case "SET_ACTIVE_MODAL":
      return { ...state, activeModal: action.payload }
    case "SET_TO_PLAN":
      return { ...state, toPlan: action.payload }
    case "SET_INTERVAL":
      return { ...state, interval: action.payload }
    case "SET_SIGNALS_OPTED_IN":
      return { ...state, signalsOptedIn: action.payload }
    default:
      return state
  }
}

export const PlanComparisonContext = createContext()

export const PlanComparisonProvider = ({
  children,
  initialPlans,
  serviceLevels = [],
  onClose,
  prioritizedServiceLevel,
  customFeatures
}) => {
  const { plans, trialing, currentPlan, planLimits, accountBillingState } =
    usePlanData(initialPlans)
  const { mutateAsync: switchPlan } = useSwitchPlan()
  const user = useCurrentUser()
  const [state, dispatch] = useReducer(planComparisonReducer, initialState)

  const trackAnalytics = (name, payload = {}) => {
    analytics.event(name, { ...payload, account_id: user.account_id })
  }

  // The opened events fire before the plans load, so they can't say whether a
  // discount is shown; the plan card views and later step events carry that.
  useEffect(() => {
    trackAnalytics("opened_plan_comparison_modal")

    analytics.track("Element", "Opened", {
      element_type: "modal",
      name: "plan_comparison_modal"
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // If an editor user on the account tries to upgrade, show a modal asking
  // them to contact their account's admin.
  useEffect(() => {
    if (!user.allowed_activities.includes("account_settings.read")) {
      dispatch({ type: "SET_ACTIVE_MODAL", payload: MODALS.UNAUTHORIZED })
    }
  }, [user])

  useEffect(() => {
    if (plans) {
      dispatch({
        type: "SET_SERVICE_LEVEL",
        payload: state.serviceLevel,
        plansForPaymentForm: plans[state.serviceLevel]
      })
    }
  }, [plans, state.serviceLevel])

  // Find all plans with valid promo codes (prioritized by backend)
  const plansWithValidPromos = plans
    ? Object.values(plans)
        .flat()
        .filter(plan => plan.prioritize_plan)
    : []

  // Determine the promo plan: try monthly first, then yearly, then fallback to first
  const promoPlan =
    plansWithValidPromos.find(plan => plan.interval === "month") ||
    plansWithValidPromos.find(plan => plan.interval === "year") ||
    plansWithValidPromos[0]

  // Derive hasAnyPromoApplied from whether we have any plans with valid promos
  const hasAnyPromoApplied = plansWithValidPromos.length > 0

  // Calculate visible plans for the current serviceLevel and interval
  const visiblePlans = serviceLevels.flatMap(serviceLevel =>
    (plans?.[serviceLevel] || []).filter(
      plan => plan.interval === (state.interval || "year")
    )
  )

  // Determine the featured plan
  const featuredPlan =
    visiblePlans.find(plan => plan.service_level === prioritizedServiceLevel) ||
    promoPlan ||
    visiblePlans.find(plan => plan.service_level === "creator")

  useEffect(() => {
    if (currentPlan && accountBillingState === "free") {
      dispatch({ type: "SET_INTERVAL", payload: "year" })
    } else if (currentPlan) {
      dispatch({ type: "SET_INTERVAL", payload: currentPlan.interval })
    }

    // Override interval if there's a promo plan with a valid promo code
    // to make sure we're showing the discounted plan
    if (promoPlan) {
      dispatch({ type: "SET_INTERVAL", payload: promoPlan.interval })
    }
  }, [currentPlan, accountBillingState, promoPlan])

  const handlePlanSelection = plan => {
    const serviceLevel = plan.service_level

    // set which plans will be offered in the payment form
    const plansForPaymentForm = plans[serviceLevel]
    dispatch({
      type: "SET_SERVICE_LEVEL",
      payload: serviceLevel,
      plansForPaymentForm
    })

    // set the selected plan interval - monthly or annual
    dispatch({ type: "SET_INTERVAL", payload: plan.interval })
    dispatch({
      type: "SET_TO_PLAN",
      payload: plan
    })

    if (accountBillingState === "paying" && !trialing) {
      // when we don't render the payment form, we show a mini plan
      // switch confirmation modal
      dispatch({
        type: "SET_ACTIVE_MODAL",
        payload: MODALS.PLAN_SWITCH_CONFIRMATION
      })
    } else {
      dispatch({ type: "SET_ACTIVE_MODAL", payload: MODALS.PAYMENT })
    }
  }

  const closeAllModals = () => {
    trackAnalytics(
      "closed_plan_comparison_modal",
      discountTrackingProperties(promoPlan)
    )

    dispatch({ type: "SET_ACTIVE_MODAL", payload: false })
    onClose?.()
  }

  const setActiveModal = (payload, extraPayload = {}) => {
    trackAnalytics("moved_to_plan_comparison_modal", {
      modal_name: payload,
      ...discountTrackingProperties(promoPlan),
      ...extraPayload
    })

    dispatch({ type: "SET_ACTIVE_MODAL", payload })
  }

  const setInterval = payload => {
    dispatch({ type: "SET_INTERVAL", payload })
  }

  const setSignalsOptedIn = payload => {
    dispatch({ type: "SET_SIGNALS_OPTED_IN", payload })
  }

  const refreshAllowedActivities = () => {
    user.refreshAllowedActivities()
  }

  const contextValue = {
    ...state,
    MODALS,
    closeAllModals,
    currentPlan,
    customFeatures,
    featuredPlan,
    handlePlanSelection,
    hasAnyPromoApplied,
    planLimits,
    plans,
    prioritizedServiceLevel,
    promoPlan,
    refreshAllowedActivities,
    serviceLevels,
    setActiveModal,
    setInterval,
    setSignalsOptedIn,
    switchPlan,
    visiblePlans
  }

  return (
    <PlanComparisonContext.Provider value={contextValue}>
      {children}
    </PlanComparisonContext.Provider>
  )
}

export const usePlanComparisonContext = () => useContext(PlanComparisonContext)
